export interface IResultSuccess<O = unknown> {
  data: O;
  ok: true;
  nil(): boolean;
}

export interface IResultFailure extends Error {
  data: null;
  ok: false;
  nil(): boolean;
}

export type Result<O = unknown> = IResultFailure | IResultSuccess<O>;

export type BuilderQuerySelector<T> =
  | Iterable<string>
  | {
      predicate: (item: string | T) => boolean;
    }
  | {
      resolver: (items: Iterable<string> | Iterable<T>) => Iterable<T>;
    };

export type IBuilderCtx = object;
export interface IBuilderAction<T> {
  execute(): T | Promise<T>;
  cancel(): boolean;
}

export type IBuilderQueryCtx = object;

export interface IBuilderVersion {
  timestamp: Date;
  store: string;
  /**
   * Compare another version with this verion.
   *   - Should return 0 if the versions are identical.
   *   - Should return 1 if the current version is newer.
   *   - Should return -1 if the the current version is older.
   */
  compare(version: IBuilderVersion): number;
  /**
   * Creates a clone of the current version.
   */
  clone(): IBuilderVersion;
}

/**
 * Represents a level in the node hierarchy.
 */
export type BuilderHierarchicalLevel = string[];

/**
 * Represents a hierarchy of node references; each key is either
 * the root of the graph or a node UUID. Each value is an array,
 * respcting ordinality, of node UUIDs.
 */
export type BuilderHierarchy = Map<string, BuilderHierarchicalLevel>;

/**
 * A level key to hierarchy entry to be used with message payload.
 */
export type BuilderHierarchyEntry = [string, BuilderHierarchicalLevel];

/*
 * A stringed enum of all available atom types.
 */
export enum IBuilderAtomTypes {
  NODE = 'node',
  EDGE = 'edge',
}

/**
 * Enum for possible edge types.
 */
export enum BuilderEdgeTypes {
  CHILD = 'child',
  FORWARD = 'forward',
}

/**
 * Optional params for constructing generic builder objects.
 */
export type IBuilderAtomConstructorParams = {
  id?: string; // UUID v4
};

/**
 * Optional params for constructing edge objects.
 */
export interface IBuilderEdgeConstructorParams
  extends IBuilderAtomConstructorParams {
  /**
   * The type of the edge.
   */
  type: BuilderEdgeTypes;
  /**
   * Source node ID for the edge.
   */
  source: string; // UUID v4
  /**
   * Source target ID for the edge.
   */
  target: string; // UUID v4
}

/**
 * An object that provides a method encoding one thing to another.
 */
export interface IEncoder<I, O> {
  encode(value: I, ...args: unknown[]): O | Promise<O>;
}

/**
 * An object that provides a method decoding one thing to another.
 */
export interface IDecoder<I, O> {
  decode(value: O, ...args: unknown[]): I | Promise<I>;
}

/**
 * Used for serializing / deserializing.
 */
export interface ICodec<I, O> {
  encode: IEncoder<I, O>['encode'];
  decode: IDecoder<I, O>['decode'];
}
